import { type AppMode, getAppMode } from '@databases/modes';
import { captureException } from '@sentry/core';

import { dynamicImport } from '@canvas/dynamic-import';

/**
 * @see databases/packages/frontend/src/common/components/database-content/Toolbar.tsx
 *
 * Because we switch toolbars based on the mode, we want to preload the lazy component
 * early in the application so we can limit the flicker as much as possible.
 */
const toolbarImports: Record<
  AppMode | 'EMBED_REFERENCE',
  ReturnType<typeof dynamicImport>
> = {
  EMBED_REFERENCE: dynamicImport(
    () => import('@databases/frontend/toolbars/EmbedReferenceToolbar')
  ),
  EMBED: dynamicImport(() => import('@databases/frontend/toolbars/EmbedToolbar')),
  MOBILE: dynamicImport(() => import('@databases/frontend/toolbars/MobileToolbar')),
  NATIVE_EMBED: dynamicImport(
    () => import('@databases/frontend/toolbars/NativeEmbedToolbar')
  ),
  STANDALONE: dynamicImport(
    () => import('@databases/frontend/toolbars/StandaloneToolbar')
  ),
} as const;

const isReferencing = (): boolean => {
  const selfQueryParams = new URLSearchParams(window.location.search);

  return selfQueryParams.get('referencing') === 'true';
};

/**
 * Determines which toolbar should be preloaded based on the current application mode.
 * For EMBED mode, checks if we're in referencing mode to determine which toolbar to use.
 */
export const getToolbarToPreload = (): AppMode | 'EMBED_REFERENCE' => {
  const appMode = getAppMode();

  if (appMode === 'EMBED' && isReferencing()) {
    return 'EMBED_REFERENCE';
  }

  return appMode;
};

/**
 * Preloads the appropriate toolbar bundle based on the current application mode.
 * This should be called during app bootstrap, before React mounts.
 */
export const preloadToolbar = () => {
  try {
    const toolbarKey = getToolbarToPreload();
    const preloadPromise = toolbarImports[toolbarKey];

    if (preloadPromise) {
      void preloadPromise;
    }
  } catch (error) {
    captureException(error, {
      tags: { context: 'preload-toolbar-bootstrap' },
    });
  }
};
